import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import Modal from 'react-modal';
import loadable from '@loadable/component';

import { AUTH_ERROR_ACCOUNT_NOT_FOUND, AUTH_ERROR_ANY } from '../../../constants/oauth';
import usePrivateRoute from '../../hooks/usePrivateRoute';
import { isLoginSelector } from '../../selector/user';

import {
  AUTH_MODAL_CLOSED,
  openAuthModal,
  closeAuthModal,
  showAccountNotFoundModal,
  showAccountExistedModal,
  showAuthErrorModal,
} from '../../reducers/ui/authModal';

const AuthModalEntry = loadable(() => import('./AuthModalEntry'));
const AuthModalSignin = loadable(() => import('./AuthModalSignin'));
const AuthModalSignup = loadable(() => import('./AuthModalSignup'));
const AuthModalAccountExisted = loadable(() => import('./AuthModalAccountExisted'));
const AuthModalAccountNotFound = loadable(() => import('./AuthModalAccountNotFound'));
const AuthModalError = loadable(() => import('./AuthModalError'));

const MAP_STATE_COMPONENT = Object.freeze({
  AUTH_MODAL_ENTRY: AuthModalEntry,
  AUTH_MODAL_SIGNIN: AuthModalSignin,
  AUTH_MODAL_SIGNUP: AuthModalSignup,
  AUTH_MODAL_ACCOUNT_EXISTED: AuthModalAccountExisted,
  AUTH_MODAL_ACCOUNT_NOT_FOUND: AuthModalAccountNotFound,
  AUTH_MODAL_ERROR: AuthModalError,
});

function replaceHistoryState(nextState = null) {
  window.history.replaceState(
    {
      ...window.history.state,
      state: nextState,
    },
    document.title
  );
}

function AuthModal(props) {
  const {
    isLogin,
    authModalState,
    openAuthModal,
    closeAuthModal,
    showAccountNotFoundModal,
    showAccountExistedModal,
    showAuthErrorModal,
  } = props;

  const location = useLocation();
  const isPrivateRoute = usePrivateRoute();
  const isModalOpen = authModalState !== AUTH_MODAL_CLOSED;

  useEffect(() => {
    if (location.state?.authError) {
      switch (location.state.authError) {
        case AUTH_ERROR_ACCOUNT_NOT_FOUND: {
          showAccountNotFoundModal();
          break;
        }

        case AUTH_ERROR_ANY: {
          showAuthErrorModal();
          break;
        }
      }

      replaceHistoryState();
    } else if (location.state?.isAccountExisted && isLogin) {
      showAccountExistedModal();
      replaceHistoryState();
    } else if (isPrivateRoute && !isLogin) {
      openAuthModal();
    } else {
      closeAuthModal();
    }
  }, [
    location,
    isLogin,
    isPrivateRoute,
    openAuthModal,
    closeAuthModal,
    showAccountExistedModal,
    showAccountNotFoundModal,
    showAuthErrorModal,
  ]);

  const Content = MAP_STATE_COMPONENT[authModalState];

  return (
    <Modal
      isOpen={isModalOpen}
      className={{
        base: 'AuthModal-content',
        afterOpen: 'AuthModal-content--after-open',
        beforeClose: 'AuthModal-content--before-close',
      }}
      overlayClassName={{
        base: 'AuthModal-overlay',
        afterOpen: 'AuthModal-overlay--after-open',
        beforeClose: 'AuthModal-overlay--before-close',
      }}
      onRequestClose={isPrivateRoute ? null : closeAuthModal}
    >
      {Content && <Content />}
    </Modal>
  );
}

function mapStateToProps(state) {
  return {
    isLogin: isLoginSelector(state),
    authModalState: state.ui.authModal,
  };
}

const mapDispatchToProps = {
  openAuthModal,
  closeAuthModal,
  showAccountExistedModal,
  showAccountNotFoundModal,
  showAuthErrorModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(AuthModal);
